import { LEVEL_LABELS } from "@itspip/shared";
import type { ObjectiveStatus, KrStatus } from "@/types/entities";
import { getFiscalLabel } from "./fiscal-year";

/**
 * Valid status transitions for Objectives and Key Results.
 * `approved` and `in_progress` can revert to `draft`; that edge is gated to
 * owner/admin in the service layer (and filtered out of the UI for others
 * via the `canRevertToDraft` prop on StatusTransitionSelect).
 * completed and killed are terminal.
 */
const VALID_TRANSITIONS: Record<ObjectiveStatus, ObjectiveStatus[]> = {
  draft: ["approved", "in_progress", "killed"],
  approved: ["draft", "in_progress", "killed"],
  in_progress: ["draft", "completed", "killed"],
  completed: [],
  killed: [],
};

export function getValidTransitions(currentStatus: ObjectiveStatus): ObjectiveStatus[] {
  return VALID_TRANSITIONS[currentStatus] ?? [];
}

export function getValidKrTransitions(currentStatus: KrStatus): KrStatus[] {
  return (VALID_TRANSITIONS as Record<KrStatus, KrStatus[]>)[currentStatus] ?? [];
}

export function isTerminalStatus(status: ObjectiveStatus | KrStatus): boolean {
  return status === "completed" || status === "killed";
}

export type StatusVariant =
  | "default"
  | "secondary"
  | "info"
  | "accent"
  | "destructive"
  | "outline"
  | "meta-level"
  | "meta-type"
  | "status-draft"
  | "status-approved"
  | "status-inprogress";

const STATUS_VARIANT_MAP: Record<ObjectiveStatus, StatusVariant> = {
  draft:       "status-draft",
  approved:    "status-approved",
  in_progress: "status-inprogress",
  completed:   "accent",
  killed:      "destructive",
};

export function getStatusVariant(status: ObjectiveStatus | KrStatus): StatusVariant {
  return STATUS_VARIANT_MAP[status] ?? "secondary";
}

const STATUS_LABELS: Record<ObjectiveStatus, string> = {
  draft: "Draft",
  approved: "Approved",
  in_progress: "In Progress",
  completed: "Completed",
  killed: "Killed",
};

export function getStatusLabel(status: ObjectiveStatus | KrStatus): string {
  return STATUS_LABELS[status] ?? status;
}

export function getLevelLabel(level: string): string {
  return (LEVEL_LABELS as Record<string, string>)[level] ?? level;
}

const TYPE_LABELS: Record<string, string> = {
  committed: "Committed",
  aspirational: "Aspirational",
};

export function getTypeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type;
}

const KR_TYPE_LABELS: Record<string, string> = {
  percentage: "Percentage",
  binary: "Binary",
  metric: "Metric",
};

export function getKrTypeLabel(type: string): string {
  return KR_TYPE_LABELS[type] ?? type;
}

const QUARTER_LABELS: Record<string, string> = {
  Q1: "Q1 (Jan–Mar)",
  Q2: "Q2 (Apr–Jun)",
  Q3: "Q3 (Jul–Sep)",
  Q4: "Q4 (Oct–Dec)",
};

export function getQuarterLabel(quarter: string, year?: number): string {
  const cyLabel = QUARTER_LABELS[quarter] ?? quarter;
  if (year == null) return cyLabel;
  const fyLabel = getFiscalLabel(quarter, year);
  return fyLabel ? `${cyLabel} \u00B7 ${fyLabel}` : cyLabel;
}
